// src/admin/hooks/useActionRequests.ts
// Reads admin_action_requests by status list, then resolves requester /
// reviewer admin profiles via a batched admin_users round-trip.
//
// Deviations from the draft plan:
//   - ActionType enum in the shipped DB is
//       `restructure_loan | write_off_loan | refund_payment
//        | reopen_cancelled_loan | adjust_credit_score`
//     (generated in database.types as `admin_action_type`), not the plan's
//     `recommend_default | adjust_credit_score | force_fail_disbursement
//      | request_restructure`. We re-export the DB-sourced union so the
//     UI stays aligned with the actual enum.
//   - ActionStatus has an extra `superseded` value beyond `pending /
//     approved / rejected`. The hook accepts any subset.
//   - `admin_users.Relationships: []` and the action-request table itself
//     has `Relationships: []`, so no PostgREST joins. Resolve requester
//     and reviewer in a single `.in('id', allIds)` call, matching the
//     pattern used by useAuditLog and useSystemConfig.
//   - Also exposes `useActionRequestsCount()` for a future topbar badge
//     (the shipped Sidebar component has no badge slot yet — adding the
//     API belongs in a later task).
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import type { Database, Json } from '@/lib/database.types';

export type ActionType = Database['public']['Enums']['admin_action_type'];
export type ActionStatus = Database['public']['Enums']['admin_action_status'];

export interface ActionRequestRow {
  id: string;
  action_type: ActionType;
  status: ActionStatus;
  target_loan_id: string | null;
  target_borrower_id: string | null;
  target_payment_id: string | null;
  request_payload: Record<string, unknown>;
  request_notes: string | null;
  requested_at: string;
  requested_by_id: string;
  requested_by: { display_name: string | null; email: string } | null;
  reviewed_at: string | null;
  reviewed_by_id: string | null;
  reviewed_by: { display_name: string | null; email: string } | null;
  review_notes: string | null;
}

interface RawActionRow {
  id: string;
  action_type: ActionType;
  status: ActionStatus;
  target_loan_id: string | null;
  target_borrower_id: string | null;
  target_payment_id: string | null;
  request_payload: Json;
  request_notes: string | null;
  requested_at: string;
  requested_by: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  review_notes: string | null;
}

export function useActionRequests(status: ActionStatus[] = ['pending']) {
  return useQuery<ActionRequestRow[]>({
    queryKey: ['action-requests', status],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_action_requests')
        .select(
          'id, action_type, status, target_loan_id, target_borrower_id, target_payment_id, request_payload, request_notes, requested_at, requested_by, reviewed_at, reviewed_by, review_notes',
        )
        .in('status', status)
        .order('requested_at', { ascending: false });
      if (error) throw error;
      const raws = (data ?? []) as unknown as RawActionRow[];
      if (raws.length === 0) return [];

      // Gather requester + reviewer ids, de-duped.
      const ids = new Set<string>();
      for (const r of raws) {
        if (r.requested_by) ids.add(r.requested_by);
        if (r.reviewed_by) ids.add(r.reviewed_by);
      }
      let adminMap = new Map<string, { display_name: string | null; email: string }>();
      if (ids.size > 0) {
        const { data: admins, error: adminErr } = await supabase
          .from('admin_users')
          .select('id, display_name, email')
          .in('id', Array.from(ids));
        if (adminErr) throw adminErr;
        adminMap = new Map(
          (admins ?? []).map((a) => [a.id, { display_name: a.display_name, email: a.email }]),
        );
      }

      return raws.map((r): ActionRequestRow => ({
        id: r.id,
        action_type: r.action_type,
        status: r.status,
        target_loan_id: r.target_loan_id,
        target_borrower_id: r.target_borrower_id,
        target_payment_id: r.target_payment_id,
        request_payload: (r.request_payload ?? {}) as Record<string, unknown>,
        request_notes: r.request_notes,
        requested_at: r.requested_at,
        requested_by_id: r.requested_by,
        requested_by: adminMap.get(r.requested_by) ?? null,
        reviewed_at: r.reviewed_at,
        reviewed_by_id: r.reviewed_by,
        reviewed_by: r.reviewed_by ? adminMap.get(r.reviewed_by) ?? null : null,
        review_notes: r.review_notes,
      }));
    },
  });
}

// Cheap head-only count for a future topbar/sidebar badge. Returns 0 while
// loading or on error so consumers can render unconditionally.
export function useActionRequestsCount() {
  return useQuery<number>({
    queryKey: ['action-requests', 'count', 'pending'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('admin_action_requests')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');
      if (error) throw error;
      return count ?? 0;
    },
  });
}
